import LoginOrCreate from "@components/User/LoginOrCreate";
import NewUser from "@components/User/New";
import Profile from "@components/User/Profile";
import SendReset from "@components/User/SendReset";
import { useColorsQuery, useUserProfileQuery } from "@graphql/api";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import ConfirmationDialog from "@hudson/ui/components//Dialogs/Confirmation";
import Dialog from "@hudson/ui/components/Dialogs/Simple";
import { camelToSnakeCase } from "@hudson/ui/lib/utils";
import styles from "@hudson/ui/styles/Form.module.css";
import Session from "@lib/session";
import useConfig from "@lib/use_config";
import useContent from "@lib/use_content";
import classNames from "classnames";
import { useSearchParams } from "next/navigation";
import { Fragment, useEffect, useState } from "react";

export default function LoggedIn(): JSX.Element | null {
  const { data: profileData } = useUserProfileQuery();
  const searchParams = useSearchParams();
  const [showDialog, setShowDialog] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const [dialogPage, setDialogPage] = useState("profile");
  const { data: clrData } = useColorsQuery();
  const { data: cfgData } = useConfig(["hideUserProfileActions"]);
  const { data: cData } = useContent([
    "beConfirmDialogDefaultCancelButton",
    "beLogoutButton",
    "beLogoutConfirmationButton",
    "beLogoutConfirmationMsg",
    "beLogoutConfirmationTitle",
    "beProfileAccountButton",
  ]);

  // If we've been directed to open the login popup via url param then do so.
  useEffect(() => {
    if (searchParams?.get("open_profile")) setShowDialog(true);
  }, []);

  let DialogComp;
  let maxWidthClass = "max-w-md";
  switch (dialogPage) {
    case "loginOrCreate":
      DialogComp = LoginOrCreate;
      break;
    case "newUser":
      DialogComp = NewUser;
      break;
    case "profile":
      DialogComp = Profile;
      maxWidthClass = "max-w-4xl";
      break;
    case "sendReset":
      DialogComp = SendReset;
      break;
  }

  async function logout() {
    await Session.clearSession();
    window.location.href = `${process.env.basePath}/${window.siteId}/book`;
  }

  function showProfileDialog() {
    setDialogPage("profile");
    setShowDialog(true);
  }

  return (
    <>
      <Dialog
        maxWidthClass={maxWidthClass}
        show={showDialog}
        onHide={() => setShowDialog(false)}
        pagePath={`/user/${camelToSnakeCase(dialogPage)}`}
      >
        <DialogComp onPageChange={(page) => setDialogPage(page)} />
      </Dialog>
      <ConfirmationDialog
        cancelLabel={cData?.content.beConfirmDialogDefaultCancelButton}
        confirmLabel={cData?.content.beLogoutConfirmationButton}
        message={cData?.content.beLogoutConfirmationMsg}
        onConfirm={logout}
        onHide={() => setShowLogout(false)}
        show={showLogout}
        title={cData?.content.beLogoutConfirmationTitle}
      />

      <Menu as="div" className="relative ml-4 shrink-0">
        <div>
          <MenuButton
            className={classNames(styles.link, "bg-white rounded-md flex focus:outline-none ")}
          >
            <div className="flex items-center p-1 space-x-2 text-lg text-gray-500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke={clrData?.colors.primaryColor}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <span>{profileData?.userProfile?.contactInfo?.name}</span>
              {cfgData?.config.hideUserProfileActions ? null : (
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" />
              )}
            </div>
          </MenuButton>
        </div>
        {cfgData?.config.hideUserProfileActions ? null : (
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <MenuItems className="absolute right-0 z-50 w-48 py-1 mt-2 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black/5 focus:outline-none">
              <MenuItem key="account">
                {({ focus }) => (
                  <a
                    href="#"
                    onClick={showProfileDialog}
                    className={classNames(
                      focus ? "bg-gray-100" : "",
                      "block py-2 px-4 text-base text-gray-700",
                    )}
                  >
                    {cData?.content.beProfileAccountButton}
                  </a>
                )}
              </MenuItem>
              <MenuItem key="logout">
                {({ focus }) => (
                  <a
                    href="#"
                    className={classNames(
                      focus ? "bg-gray-100" : "",
                      "block py-2 px-4 text-base text-gray-700",
                    )}
                    onClick={() => setShowLogout(true)}
                  >
                    {cData?.content.beLogoutButton}
                  </a>
                )}
              </MenuItem>
            </MenuItems>
          </Transition>
        )}
      </Menu>
    </>
  );
}
